import React, { useContext, useCallback, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { useLoaderData } from 'react-router-dom'
import { enqueueSnackbar } from 'notistack'
import { DefaultFetchDeviceFilters, DurationTestStatus } from '@/api/definition'
import DeviceSelectionTable, {
  type ValidationConfig,
  DeviceData,
} from '@/components/DeviceSelectionTable'
import type { IntakeLoaderData } from '@/pages/intake/loaders'
import { SelectedDevicesContext } from '@/pages/intake/reducers'
import { useDeviceTypeFilters } from '@/hooks/useTransalationForFilter'
import type { Device } from '@/pages/Device/Device'

interface SelectDevicesProps {
  setActiveStep: (step: number) => void
}
interface RowSelectableParams {
  row: Device
  id?: string | number
  [key: string]: unknown
}
const SelectDevicesForIntakeStep: React.FC<SelectDevicesProps> = ({
  setActiveStep,
}) => {
  const { t } = useTranslation(['di', 'app', 'common'])
  const { initialDevices, rowCount, loaderError } =
    useLoaderData<IntakeLoaderData>()
  const { state: selectedDevicesState, dispatch: selectedDevicesDispatch } =
    useContext(SelectedDevicesContext)

  const selectedDeviceIds = useMemo(
    () => Object.keys(selectedDevicesState.selectedDevices),
    [selectedDevicesState],
  )

  const deviceFilters = useMemo(
    () => ({
      ...DefaultFetchDeviceFilters,
      inService: false,
      durationTestStatus: [DurationTestStatus.DurationTestNotStarted],
    }),
    [],
  )

  const validateDeviceEditable = useCallback(
    (device: Device): boolean => {
      if (device.editable) {
        return true
      }
      enqueueSnackbar(t('di:device.rights.producerOnlyError'), {
        variant: 'error',
      })
      return false
    },
    [t],
  )
  const filterConfig = useDeviceTypeFilters()

  const columnConfig = useMemo(
    () => ({
      includeProjectName: true,
    }),
    [],
  )

  const handleSelectionChange = useCallback(
    (newSelectedDevices: DeviceData[], deselectedIds: string[]) => {
      if (deselectedIds.length > 0) {
        selectedDevicesDispatch({
          type: 'DESELECT_MULTIPLE_DEVICES',
          uids: deselectedIds,
        })
      }
      if (newSelectedDevices.length > 0) {
        selectedDevicesDispatch({
          type: 'SELECT_MULTIPLE_DEVICES',
          devices: newSelectedDevices,
        })
      }
    },
    [selectedDevicesDispatch],
  )

  const validationConfig: ValidationConfig = useMemo(
    () => ({
      validateSelection: validateDeviceEditable,
      isRowSelectable: (params: RowSelectableParams) => params.row.editable,
      validationErrorMessage: t('di:device.deviceCannotBeEdited', {
        count: '{count}',
      }),
    }),
    [validateDeviceEditable, t],
  )
  const handleNext = useCallback(() => {
    setActiveStep(1)
  }, [setActiveStep])

  return (
    <DeviceSelectionTable
      initialDevices={initialDevices}
      rowCount={rowCount}
      selectedDeviceIds={selectedDeviceIds}
      onSelectionChange={handleSelectionChange}
      onNext={handleNext}
      onRowClick={validateDeviceEditable}
      loaderError={loaderError}
      deviceFilters={deviceFilters}
      columnConfig={columnConfig}
      validationConfig={validationConfig}
      sharingConfig={{
        shareFilters: false,
        shareSearch: false,
        enableFilters: true,
        enableSearch: true,
      }}
      filterConfig={filterConfig}
      nextButtonProps={{
        text: t('common:general.next'),
        tooltip: t('app:device.intake.willPutAllIntoService'),
        color: 'success',
      }}
    />
  )
}

export default SelectDevicesForIntakeStep
